/* =============================================================
   FOOTER — Step 8 update: Services, Resources, Contact columns
   Design: Dark navy, gold accents, credential display
   All links scroll to top of destination page
   ============================================================= */

import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin, ArrowRight } from "lucide-react";
import { LOGOS_OPTIMIZED, LOGO_DIMENSIONS } from "@/const/logosOptimized";

// Helper: navigate to a URL and scroll to top
const handleNavClick = (href: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
  // For anchor links on the same page, just let the browser handle it
  if (href.startsWith("/#") || href.startsWith("#")) return;
  e.preventDefault();
  window.scrollTo({ top: 0, behavior: "instant" });
  // Small delay to let scroll happen before navigation
  setTimeout(() => { window.location.href = href; }, 10);
};

export default function Footer() {
  const currentYear = new Date().getFullYear();

  const services = [
    { label: "Bookkeeping Services Perth", href: "/bookkeeping-services-perth" },
    { label: "BAS Agent Perth", href: "/bas-agent-perth" },
    { label: "Xero Bookkeeper Perth", href: "/xero-bookkeeper-perth" },
    { label: "Payroll Bookkeeper Perth", href: "/payroll-bookkeeper-perth" },
    { label: "Catch-up Bookkeeping Perth", href: "/catch-up-bookkeeping-perth" },
    { label: "Bookkeeping for Tradies Perth", href: "/bookkeeping-for-tradies-perth" },
  ];

  const resources = [
    { label: "Blog", href: "/blog" },
    { label: "How Much Does a Bookkeeper Cost in Perth?", href: "/blog/how-much-does-a-bookkeeper-cost-in-perth" },
    { label: "BAS Preparation Checklist", href: "/blog/bas-preparation-checklist-small-business" },
    { label: "Xero Setup Guide", href: "/blog/xero-setup-small-business-perth" },
    { label: "Bookkeeping for Tradies", href: "/blog/bookkeeping-for-perth-tradies" },
  ];

  return (
    <footer className="bg-[oklch(0.16_0.05_250)] text-white">
      {/* Main footer content */}
      <div className="container py-14 lg:py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10">

          {/* Brand column */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <picture>
                <source srcSet={LOGOS_OPTIMIZED.standard.webp} type="image/webp" />
                <img 
                  src={LOGOS_OPTIMIZED.standard.png}
                  srcSet={`${LOGOS_OPTIMIZED.standard.png} 1x, ${LOGOS_OPTIMIZED.retina.png} 2x`}
                  alt="The Perth Bookkeeper" 
                  className="w-8 h-8 flex-shrink-0"
                  width={LOGO_DIMENSIONS.standard.width}
                  height={LOGO_DIMENSIONS.standard.height}
                  loading="eager"
                  decoding="async"
                />
              </picture>
              <span className="text-white font-semibold" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                The Perth Bookkeeper
              </span>
            </div>
            <p className="text-white/70 text-sm leading-relaxed mb-3 max-w-xs" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
              Perth's trusted bookkeeping partner for small businesses. Accurate, timely, and stress-free financial records.
            </p>
            <p className="text-white/60 text-xs leading-relaxed mb-6 max-w-xs" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
              Serving Perth small businesses across the metro area and wider WA.
            </p>

            {/* Social links */}
            <div className="flex gap-3">
              <a
                href="https://www.facebook.com/share/9gWerNmxxFZCWpxg/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center hover:bg-[oklch(0.72_0.12_75)/0.3] transition-colors"
                aria-label="Facebook"
              >
                <Facebook size={15} />
              </a>
              <a
                href="https://www.instagram.com/the_perth_bookkeeper"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center hover:bg-[oklch(0.72_0.12_75)/0.3] transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={15} />
              </a>
              <a
                href="https://www.linkedin.com/in/agnieszka-peszel"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center hover:bg-[oklch(0.72_0.12_75)/0.3] transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin size={15} />
              </a>
            </div>
          </div>

          {/* Services column */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Services
            </h4>
            <ul className="space-y-2.5">
              {services.map((item) => (
                <li key={item.label}>
                  <a
                    href={item.href}
                    onClick={handleNavClick(item.href)}
                    className="text-white/65 hover:text-[oklch(0.72_0.12_75)] text-sm transition-colors"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources column */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Resources
            </h4>
            <ul className="space-y-2.5">
              {resources.map((item) => (
                <li key={item.label}>
                  <a
                    href={item.href}
                    onClick={handleNavClick(item.href)}
                    className="text-white/65 hover:text-[oklch(0.72_0.12_75)] text-sm transition-colors leading-snug block"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact column */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Contact
            </h4>
            <div className="space-y-3">
              <a href="tel:0424200785" className="flex items-center gap-2.5 text-white/65 hover:text-[oklch(0.72_0.12_75)] transition-colors">
                <Phone size={13} className="flex-shrink-0" />
                <span className="text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>0424 200 785</span>
              </a>
              <a href="mailto:hello@theperthbookkeeper.com.au" className="flex items-start gap-2.5 text-white/65 hover:text-[oklch(0.72_0.12_75)] transition-colors">
                <Mail size={13} className="flex-shrink-0 mt-0.5" />
                <span className="text-sm break-all" style={{ fontFamily: "'DM Sans', sans-serif" }}>hello@theperthbookkeeper.com.au</span>
              </a>
              <div className="flex items-start gap-2.5 text-white/60">
                <MapPin size={13} className="flex-shrink-0 mt-0.5" />
                <span className="text-sm leading-snug" style={{ fontFamily: "'DM Sans', sans-serif" }}>Perth, WA — serving metro &amp; wider WA</span>
              </div>
            </div>

            <div className="mt-5 pt-5 border-t border-white/10">
              <div className="text-white/50 text-xs mb-2" style={{ fontFamily: "'DM Mono', monospace", letterSpacing: '0.1em' }}>
                CREDENTIALS
              </div>
              <div className="space-y-1">
                <div className="text-white/60 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>BAS Agent: 262 458 37</div>
                <div className="text-white/60 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>ABN: 87 148 349 750</div>
              </div>
            </div>

            <div className="mt-5">
              <a href="/#contact" className="inline-flex items-center gap-2 text-[oklch(0.72_0.12_75)] text-sm font-semibold hover:gap-3 transition-all" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                Book Free Consultation
                <ArrowRight size={14} />
              </a>
            </div>
          </div>

        </div>
      </div>

      {/* Service Areas bar */}
      <div className="border-t border-white/10 bg-[oklch(0.13_0.04_250)]">
        <div className="container py-6">
          <p className="text-white/50 text-xs mb-3" style={{ fontFamily: "'DM Mono', monospace", letterSpacing: '0.1em' }}>SERVICE AREAS</p>
          <p className="text-white/60 text-xs leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
            Perth CBD · East Perth · West Perth · Joondalup · Osborne Park · Subiaco · Canning Vale · Fremantle · Midland · Balcatta · Malaga · Wangara · Rockingham · Mandurah · Remote WA Businesses
          </p>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="container py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-white/50 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            © {currentYear} The Perth Bookkeeper. All rights reserved.
          </p>
          <div className="flex gap-5">
            <a href="/privacy-policy" onClick={handleNavClick('/privacy-policy')} className="text-white/50 hover:text-white/70 text-xs transition-colors" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Privacy Policy
            </a>
            <a href="/website-terms" onClick={handleNavClick('/website-terms')} className="text-white/50 hover:text-white/70 text-xs transition-colors" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Website Terms
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
